// M1.3 smoke test: drop an RFQ-shaped artifact (PDF, image, .msg-as-text), optionally
// fill in email metadata, click Extract — Brain calls /api/brain/extract task=rfq_from_email
// and renders the parsed result.
//
// This is intentionally a flat test surface, not the final UX. Module 5 (CRM) Phase 5.3
// builds the proper RFQ-intake → quote-draft flow on top of this same Brain task.

import { useState, useRef, type DragEvent, type ChangeEvent } from 'react';
import { Brain } from '@/sdk/brain';
import type { BrainError, ExtractedRfq, RfqAttachment } from '@/sdk/brain';
import { expandMsgFile, isMsgFile } from '@/shared/msgParser';
import { uploadRfqAttachment } from '@/shared/drawingStorage';

interface PendingAttachment extends RfqAttachment {
  sizeBytes: number;
  /** Original File handle for non-.msg drops — used to upload to the RFQ
   *  pool so the URL travels with the eventual Quote. Null for attachments
   *  unpacked from a .msg (we already have base64 data; upload would need
   *  a roundtrip through a Blob). Storage upload of .msg-unpacked
   *  attachments is a separate enhancement. */
  file?: File;
  /** Firebase Storage download URL — set after a successful upload. When
   *  this is empty the attachment still goes to the AI for extraction but
   *  isn't part of the quote's drawing pool. */
  uploadedUrl?: string;
}

/** Lightweight UUID for the in-flight RFQ. crypto.randomUUID is available
 *  in all evergreen browsers and Node 14.17+. Falls back to a timestamp
 *  if for some reason it's missing. */
function newRfqLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `rfq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function fileToAttachment(file: File): Promise<PendingAttachment> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is 'data:<mime>;base64,<b64>' — strip the prefix
      const r = reader.result as string;
      const comma = r.indexOf(',');
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
  return {
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    data,
    sizeBytes: file.size,
    file,
  };
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export function RfqExtractTest() {
  const [sender, setSender] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ExtractedRfq | null>(null);
  const [usage, setUsage] = useState<{ tokensIn: number; tokensOut: number; latencyMs: number; model: string } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // rfqLocalId is minted lazily on first file drop — keeps an unused UUID
  // out of state when the user only pastes text. Held in a ref so successive
  // drops reuse the same pool folder. Cleared on every new extract run via
  // the reset path below.
  const rfqLocalIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function ensureRfqLocalId(): string {
    if (!rfqLocalIdRef.current) {
      rfqLocalIdRef.current = newRfqLocalId();
    }
    return rfqLocalIdRef.current;
  }

  async function handleFiles(files: FileList | File[]) {
    setError(null);
    const arr = Array.from(files);
    const next: PendingAttachment[] = [];
    const notices: string[] = [];
    // Files we'll upload to the RFQ pool in the background. Non-.msg drops
    // get uploaded so the resulting download URLs travel with the quote
    // when Justin hits Create Quote. .msg-unpacked attachments are skipped
    // for now (data is base64 in memory, not a File handle — a separate
    // pass through a Blob would be needed to upload them).
    const toUpload: { name: string; file: File }[] = [];

    for (const f of arr) {
      // .msg files — Outlook proprietary format. Unpack client-side into
      // email metadata + embedded attachments, then enqueue the unpacked
      // pieces. The .msg itself is dropped — its contents have been extracted.
      if (isMsgFile(f)) {
        try {
          const expanded = await expandMsgFile(f);
          // Auto-fill form fields from the .msg headers (only if currently empty —
          // don't clobber user-entered values)
          if (expanded.email.sender && !sender) setSender(expanded.email.sender);
          if (expanded.email.senderEmail && !senderEmail) setSenderEmail(expanded.email.senderEmail);
          if (expanded.email.subject && !subject) setSubject(expanded.email.subject);
          if (expanded.email.body && !emailBody) setEmailBody(expanded.email.body);

          for (const att of expanded.attachments) {
            next.push(att);
          }
          notices.push(
            `Unpacked ${f.name}: ${expanded.attachments.length} embedded attachment${expanded.attachments.length === 1 ? '' : 's'}, email headers auto-filled.`,
          );
        } catch (e) {
          setError(`Failed to parse Outlook .msg "${f.name}": ${(e as Error).message}`);
        }
        continue;
      }

      try {
        const att = await fileToAttachment(f);
        next.push(att);
        toUpload.push({ name: att.name, file: f });
      } catch (e) {
        setError(`Failed to read "${f.name}": ${(e as Error).message}`);
      }
    }

    setAttachments((prev) => [...prev, ...next]);
    if (notices.length > 0) {
      setNotice(notices.join(' '));
    }

    // Fire-and-forget pool uploads — once each completes, patch the
    // matching attachment's uploadedUrl in state. Doesn't block the user
    // from hitting Extract: the AI call uses base64 data inline. The
    // upload only matters when they Create Quote afterwards.
    if (toUpload.length > 0) {
      const localId = ensureRfqLocalId();
      for (const { name, file } of toUpload) {
        void uploadRfqAttachment(localId, file)
          .then((res) => {
            setAttachments((prev) =>
              prev.map((a) =>
                a.name === name && !a.uploadedUrl ? { ...a, uploadedUrl: res.url } : a,
              ),
            );
          })
          .catch((e) => {
            // Soft-fail — log only. Extract still works, just the quote's
            // attachedDrawings pool will be short an entry.
            // eslint-disable-next-line no-console
            console.warn(`RFQ pool upload failed for "${name}":`, e);
          });
      }
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) {
      void handleFiles(e.dataTransfer.files);
    }
  }

  function onFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      void handleFiles(e.target.files);
      e.target.value = '';
    }
  }

  function removeAttachment(idx: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  async function runExtract() {
    if (!emailBody.trim() && attachments.length === 0) {
      setError('Add an email body OR at least one attachment to extract from.');
      return;
    }
    setRunning(true);
    setError(null);
    setNotice(null);
    setResult(null);
    setUsage(null);

    try {
      const res = await Brain.extract<ExtractedRfq>({
        task: 'rfq_from_email',
        input: {
          sender,
          senderEmail,
          subject,
          body: emailBody,
          attachments: attachments.map(({ sizeBytes: _sizeBytes, ...rest }) => rest),
        },
      });
      setResult(res.parsed);
      setUsage({
        tokensIn: res.usage.tokensIn,
        tokensOut: res.usage.tokensOut,
        latencyMs: res.usage.latencyMs,
        model: res.usage.model,
      });
    } catch (err) {
      const be = err as BrainError;
      setError(`${be.type ?? 'unknown'}: ${be.message ?? String(err)}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="bg-paper border border-line rounded p-4">
      <h2 className="font-semibold text-navy mb-1">RFQ extraction smoke test (M1.3)</h2>
      <p className="text-xs text-text-mute mb-4">
        Drop a PDF or image, optionally fill in email headers, click Extract. Calls{' '}
        <code className="bg-bg px-1 rounded">Brain.extract({'{'} task: 'rfq_from_email' {'}'})</code>.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">From name</label>
          <input
            type="text"
            value={sender}
            onChange={(e) => setSender(e.target.value)}
            placeholder="Sarah Lee"
            className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </div>
        <div>
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">From email</label>
          <input
            type="email"
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            placeholder="sarah@acme.com"
            className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="RFQ — 50 bracket assemblies"
            className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">Email body</label>
          <textarea
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            placeholder="Hi Justin, please quote 50, 100, and 250 of part BRK-2245 per the attached drawing..."
            rows={4}
            className="w-full px-2 py-1 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </div>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded p-6 text-center text-sm transition-colors cursor-pointer ${
          dragOver ? 'border-orange bg-orange/5' : 'border-line-dark text-text-mute hover:border-orange/50'
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onFileInputChange}
          className="hidden"
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.dxf,.step,.stp,.csv,.txt,.json,.msg"
        />
        📎 Drop PDFs / images / DXFs / Outlook .msg files here — or click to pick
      </div>

      {notice && (
        <div className="mt-3 bg-green/10 border-l-4 border-green text-text px-3 py-2 text-sm">
          ✓ {notice}
        </div>
      )}

      {attachments.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {attachments.map((a, i) => (
            <li key={i} className="flex items-center justify-between bg-bg px-2 py-1 rounded">
              <span className="font-mono text-xs truncate flex-1">
                {a.name} <span className="text-text-mute">({a.mimeType}, {fmtBytes(a.sizeBytes)})</span>
              </span>
              <button
                onClick={() => removeAttachment(i)}
                className="text-text-mute hover:text-orange text-xs ml-2"
                aria-label={`Remove ${a.name}`}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={runExtract}
          disabled={running}
          className="px-4 py-2 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          {running ? 'Extracting…' : 'Extract RFQ'}
        </button>
        {usage && (
          <span className="text-xs text-text-mute font-mono">
            {usage.tokensIn} in / {usage.tokensOut} out · {usage.latencyMs}ms · {usage.model}
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
          ⚠ {error}
        </div>
      )}

      {result && (
        <div className="mt-4 border-t border-line pt-4">
          <h3 className="font-semibold text-navy text-sm mb-2">Parsed result</h3>
          {result.summary && (
            <div className="bg-bg p-3 rounded mb-3 text-sm">
              <div className="text-xs text-text-mute uppercase tracking-wide mb-1">Summary</div>
              {result.summary}
            </div>
          )}
          <pre className="bg-navy text-white p-3 rounded text-xs overflow-x-auto max-h-96 overflow-y-auto font-mono">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
